import { lumi } from '@/lib/lumi'

export type TradeKind = 'curve' | 'presale'
export type TradeSide = 'buy' | 'sell'

export interface TradeActivityRecord {
  id: string
  projectKey: string
  kind: TradeKind
  wallet: string
  side: TradeSide
  tokenAmount: string
  quoteAmount: string
  txHash?: string
  createdAt: string
}

export interface ReportTradeActivityInput {
  projectKey: string
  kind: TradeKind
  wallet: string
  side: TradeSide
  tokenAmount: string
  quoteAmount: string
  txHash?: string
}

const cache = new Map<string, TradeActivityRecord[]>()
const listeners = new Set<(key: string) => void>()

function cacheKey(projectKey: string, kind: TradeKind) {
  return `${kind}:${projectKey.trim().toLowerCase()}`
}

export function onTradeActivityRecorded(listener: (key: string) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function notify(key: string) {
  listeners.forEach(listener => listener(key))
}

/**
 * Fire-and-forget: records a completed buy/sell/contribution so the project
 * page can show "Recent buys & sells" straight from our own database,
 * without any extra Blockscout/RPC calls to reconstruct trade history.
 * Never throws — a logging failure must never block a trade.
 */
export function reportTradeActivity(input: ReportTradeActivityInput): void {
  const projectKey = input.projectKey?.trim()
  const wallet = input.wallet?.trim()
  if (!projectKey || !wallet)
    return
  void lumi.functions.invoke('tradeActivity', {
    method: 'POST',
    body: {
      mode: 'record',
      projectKey,
      kind: input.kind,
      wallet,
      side: input.side,
      tokenAmount: input.tokenAmount,
      quoteAmount: input.quoteAmount,
      txHash: input.txHash,
    },
  })
    .then(() => {
      const key = cacheKey(projectKey, input.kind)
      cache.delete(key)
      notify(key)
    })
    .catch(() => {
      // Best-effort activity log only; silently ignore failures.
    })
}

export async function listTradeActivity(
  projectKey: string | undefined,
  kind: TradeKind,
  limit = 15,
): Promise<TradeActivityRecord[]> {
  const key = projectKey?.trim()
  if (!key)
    return []
  const mapKey = cacheKey(key, kind)
  const cached = cache.get(mapKey)
  if (cached)
    return cached
  try {
    const response = await lumi.functions.invoke('tradeActivity', {
      method: 'POST',
      body: { mode: 'list', projectKey: key, kind, limit },
    })
    const trades = Array.isArray((response as any)?.trades) ? (response as any).trades as TradeActivityRecord[] : []
    cache.set(mapKey, trades)
    return trades
  }
  catch {
    return cache.get(mapKey) ?? []
  }
}
